import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { ISlaListQuery } from "@/services/sla.service";
import type { RootStore } from "./root.store";
import type {
  ISla,
  ISlaActivity,
  ISlaCreatePayload,
  ISlaUpdatePayload,
  ISlaWorkflowCondition,
  ISlaWorkflowCoverage,
} from "@/types/sla";

/** Server-driven SLA settings list (search / sort / cursor pagination), plus the
 *  active-workflow options the conditions editor scopes an SLA to. */
export class SlaStore {
  slas: ISla[] = [];
  pageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  /** Active workflows, the option set for an SLA's conditions (NG-15 D2 — an SLA
   *  stays attached to a workflow that is later deactivated, but a *new*
   *  condition may only name an active one). */
  workflowOptions: ISlaWorkflowCondition[] = [];
  /** True once a `loadWorkflowOptions` call has settled, so a consumer can tell
   *  "no active workflows" from "not fetched yet" — an empty list means opposite
   *  things in those two states. */
  workflowOptionsLoaded = false;
  /** Which SLA already claims each workflow (NG-208). Read by both pickers of the
   *  one-SLA-per-workflow rule, to lock an option the save would refuse rather
   *  than let it be chosen. Never the enforcement — another author can claim a
   *  workflow between this read and the save, which is why the server-side
   *  validation stays exactly as it is. */
  workflowCoverage: ISlaWorkflowCoverage[] = [];
  isPageLoading = false;
  isLoadingMore = false;
  pageError: Error | null = null;
  isSaving = false;
  activeSlaId: string | null = null;

  private lastPageQuery: ISlaListQuery = {};
  // Monotonic load token for latest-wins: the list route fires an unfiltered load
  // on mount while the search box fires its own filtered load, with no dedup
  // between them. Tag each load and apply only the latest, so a slow earlier
  // response cannot clobber a newer one. (#1809)
  private loadToken = 0;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        slas: observable.ref,
        workflowCoverage: observable.ref,
        lastPageQuery: false,
        loadToken: false,
      } as never,
      { autoBind: true },
    );
  }

  /** Load the first page with the given server query, replacing the current page. */
  async loadPage(slug: string, query: ISlaListQuery = {}): Promise<void> {
    const token = ++this.loadToken;
    const isLatest = () => this.loadToken === token;
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const page = await this.rootStore.slaService.listPage(slug, { ...query, cursor: null });
      runInAction(() => {
        if (!isLatest()) return; // superseded by a newer load
        this.slas = page.results;
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        if (isLatest()) this.pageError = error;
      });
      console.error("[sla] failed to load SLA list", { slug, query, error });
      throw error;
    } finally {
      runInAction(() => {
        if (isLatest()) this.isPageLoading = false;
      });
    }
  }

  /** Append the next page (DataTable infinite scroll); re-runs the first page
   *  when nothing is loaded yet (the error-empty "Try again" path). */
  async loadMore(slug: string): Promise<void> {
    if (this.slas.length === 0) {
      await this.loadPage(slug, this.lastPageQuery);
      return;
    }
    if (!this.pageInfo.hasNext || !this.pageInfo.nextCursor || this.isLoadingMore) return;
    // Append is valid only while no newer first-page load supersedes it. (#1809)
    const loadToken = this.loadToken;
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const page = await this.rootStore.slaService.listPage(slug, {
        ...this.lastPageQuery,
        cursor: this.pageInfo.nextCursor,
      });
      runInAction(() => {
        if (this.loadToken !== loadToken) return;
        const seen = new Set(this.slas.map((s) => s.id));
        this.slas = [...this.slas, ...page.results.filter((s) => !seen.has(s.id))];
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        this.pageError = error;
      });
      console.error("[sla] failed to load more SLAs", { slug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /**
   * Load every page into the bank — for a surface using the list as an OPTION
   * SET rather than a scrollable table (the Form Details SLAs picker), where a
   * row past the first page is not "not loaded yet", it is unselectable.
   *
   * Walks with the existing `loadMore` so the latest-wins token still applies,
   * and bounds the walk: a workspace past the bound wants server-side search,
   * and the warn is what says so instead of truncating in silence.
   */
  async loadAllPages(slug: string, query: ISlaListQuery = {}): Promise<void> {
    const MAX_PAGES = 10;
    await this.loadPage(slug, query);
    // Every check after the first await runs outside the action opened by this
    // method. Snapshot the observable pagination state inside its own action so
    // the option-bank walk stays quiet under observableRequiresReaction.
    const readProgress = () =>
      runInAction(() => ({
        hasNext: this.pageInfo.hasNext,
        loaded: this.slas.length,
        totalCount: this.pageInfo.totalCount,
      }));
    let pages = 1;
    let progress = readProgress();
    while (progress.hasNext && pages < MAX_PAGES) {
      await this.loadMore(slug);
      pages += 1;
      progress = readProgress();
    }
    if (progress.hasNext) {
      console.warn("[sla] SLA option bank truncated; switch to server-side search", {
        slug,
        loaded: progress.loaded,
        totalCount: progress.totalCount,
        maxPages: MAX_PAGES,
      });
    }
  }

  slaById(slaId: string): ISla | undefined {
    return this.slas.find((s) => s.id === slaId);
  }

  /**
   * Load the active workflows an SLA can be scoped to (NG-15 §5.2). The picker
   * searches client-side, so the whole option set is fetched up front, walking
   * the cursor rather than trusting one page: a workspace past `per_page` active
   * workflows would otherwise silently lose the tail from the options, and a
   * condition you cannot see is a condition you cannot fix. The walk is bounded
   * — a workspace beyond it wants server-side typeahead (`CommandSelect`), and
   * the warn is what says so out loud instead of truncating in silence.
   */
  /** Refresh the workflow -> owning-SLA map. Failure clears it and logs rather
   *  than rejecting: with no map the pickers simply offer everything and the save
   *  still refuses an overlap, which is the pre-NG-208 behaviour — degraded, not
   *  broken. Locking options off a stale map would be the worse failure. */
  async loadWorkflowCoverage(slug: string): Promise<void> {
    try {
      const coverage = await this.rootStore.slaService.listWorkflowCoverage(slug);
      runInAction(() => {
        this.workflowCoverage = coverage;
      });
    } catch (err) {
      runInAction(() => {
        this.workflowCoverage = [];
      });
      console.warn("[sla] could not load which SLA claims each workflow", { slug, err });
    }
  }

  async loadWorkflowOptions(slug: string): Promise<void> {
    const PER_PAGE = 100;
    const MAX_PAGES = 5;
    try {
      const collected: ISlaWorkflowCondition[] = [];
      let cursor: string | null = null;
      let page = 0;
      let truncated = false;
      do {
        const result = await this.rootStore.intakeService.listFormsPage(slug, {
          is_active: true,
          per_page: PER_PAGE,
          cursor,
          order_by: "name",
        });
        collected.push(...result.results.map((form) => ({ id: form.id, name: form.name })));
        cursor = result.next_page_results ? result.next_cursor || null : null;
        page += 1;
        truncated = Boolean(cursor) && page >= MAX_PAGES;
      } while (cursor && page < MAX_PAGES);
      if (truncated) {
        console.warn("[sla] active-workflow options truncated; switch to server-side search", {
          slug,
          loaded: collected.length,
          maxPages: MAX_PAGES,
        });
      }
      runInAction(() => {
        this.workflowOptions = collected;
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      // Recoverable: the editor still renders, with the SLA's existing pinned
      // conditions and an empty option list.
      console.warn("[sla] failed to load active workflow options", { slug, error });
    } finally {
      runInAction(() => {
        this.workflowOptionsLoaded = true;
      });
    }
  }

  /**
   * Replace the SLAs covering one workflow (NG-15 §3.2, the Form Details field).
   *
   * Rethrows so the caller can surface the server's `{error, code}` inline — an
   * assignment can be rejected for reasons only the server knows (an overlap, a
   * workflow it would leave uncovered, or an SLA the caller may not reassign),
   * which is the documented exception to the optimistic-write default.
   */
  async setWorkflowSlas(
    slug: string,
    formId: string,
    slaIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<string[]> {
    this.isSaving = true;
    try {
      const saved = await this.rootStore.slaService.setWorkflowSlas(slug, formId, slaIds);
      // The form's SLAs are a field of the form, as NG-2058 reports its other edits.
      track("form_updated", { form_id: formId, fields: ["slas"], count: slaIds.length, source });
      return saved;
    } catch (err) {
      console.error("[sla] failed to set the workflow's SLAs", {
        slug,
        formId,
        slaIds,
        error: err,
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Fetch a single SLA (edit-page load). Caches it into the list bank so the
   *  details form and any rendered list row stay consistent. */
  async fetchSla(slug: string, slaId: string): Promise<ISla> {
    this.activeSlaId = slaId;
    try {
      const sla = await this.rootStore.slaService.get_(slug, slaId);
      runInAction(() => {
        const exists = this.slas.some((s) => s.id === slaId);
        this.slas = exists ? this.slas.map((s) => (s.id === slaId ? sla : s)) : [...this.slas, sla];
      });
      return sla;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[sla] failed to fetch SLA", { slug, slaId, error });
      throw error;
    } finally {
      runInAction(() => {
        this.activeSlaId = null;
      });
    }
  }

  async createSla(
    slug: string,
    data: ISlaCreatePayload,
    source: AnalyticsSource = "page",
  ): Promise<ISla> {
    this.isSaving = true;
    try {
      const sla = await this.rootStore.slaService.create(slug, data);
      runInAction(() => {
        this.slas = [sla, ...this.slas];
        this.pageInfo = { ...this.pageInfo, totalCount: this.pageInfo.totalCount + 1 };
      });
      track("sla_created", { sla_id: sla.id, source });
      return sla;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async updateSla(
    slug: string,
    slaId: string,
    data: ISlaUpdatePayload,
    source: AnalyticsSource = "page",
  ): Promise<ISla> {
    this.isSaving = true;
    this.activeSlaId = slaId;
    // Read before the write: `fields` is what this save changed (NG-2064). The
    // record holds its workflows as conditions; the payload sends their ids.
    const cached = this.slas.find((s) => s.id === slaId);
    const fields = changedFields(
      data,
      cached && { ...cached, workflow_ids: cached.workflow_conditions.map((w) => w.id) },
    );
    try {
      const sla = await this.rootStore.slaService.update(slug, slaId, data);
      runInAction(() => {
        this.slas = this.slas.map((s) => (s.id === slaId ? sla : s));
      });
      // The detail page's Active switch sends is_active alone: an activation,
      // not an edit of the SLA's terms.
      const onlyActive = Object.keys(data).length === 1 && data.is_active !== undefined;
      if (onlyActive) {
        track(data.is_active ? "sla_activated" : "sla_deactivated", { sla_id: slaId, source });
      } else {
        track("sla_updated", { sla_id: slaId, fields, source });
      }
      return sla;
    } finally {
      runInAction(() => {
        this.isSaving = false;
        this.activeSlaId = null;
      });
    }
  }

  async deleteSla(slug: string, slaId: string, source: AnalyticsSource = "page"): Promise<void> {
    this.activeSlaId = slaId;
    try {
      await this.rootStore.slaService.remove(slug, slaId);
      track("sla_deleted", { sla_id: slaId, source });
      runInAction(() => {
        const before = this.slas.length;
        this.slas = this.slas.filter((s) => s.id !== slaId);
        if (this.slas.length < before) {
          this.pageInfo = {
            ...this.pageInfo,
            totalCount: Math.max(0, this.pageInfo.totalCount - 1),
          };
        }
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error("[sla] failed to delete SLA", { slug, slaId, error });
      throw error;
    } finally {
      runInAction(() => {
        this.activeSlaId = null;
      });
    }
  }
}

interface SlaActivityFeed {
  entries: ISlaActivity[];
  hasNext: boolean;
  nextCursor: string | null;
}

/** Per-SLA activity feed (newest-first, cursor-paginated), keyed by SLA id.
 *  Powers the history side panel on the SLA detail page. */
export class SlaActivityStore {
  feedBySlaId = observable.map<string, SlaActivityFeed>();
  isLoading = false;
  isLoadingMore = false;
  error: Error | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  entries(slaId: string): ISlaActivity[] {
    return this.feedBySlaId.get(slaId)?.entries ?? [];
  }

  hasNext(slaId: string): boolean {
    return this.feedBySlaId.get(slaId)?.hasNext ?? false;
  }

  /** Load (or revalidate) the first page for an SLA, replacing any cached feed. */
  async loadFirstPage(slug: string, slaId: string): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const page = await this.rootStore.slaService.listActivityPage(slug, slaId, { cursor: null });
      runInAction(() => {
        this.feedBySlaId.set(slaId, {
          entries: page.results,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        });
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        this.error = error;
      });
      console.error("[sla] failed to load SLA activity", { slug, slaId, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Append the next (older) page for an SLA. No-op when nothing more to load. */
  async loadMore(slug: string, slaId: string): Promise<void> {
    const feed = this.feedBySlaId.get(slaId);
    if (!feed?.hasNext || !feed.nextCursor || this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.error = null;
    try {
      const page = await this.rootStore.slaService.listActivityPage(slug, slaId, {
        cursor: feed.nextCursor,
      });
      runInAction(() => {
        const seen = new Set(feed.entries.map((e) => e.id));
        this.feedBySlaId.set(slaId, {
          entries: [...feed.entries, ...page.results.filter((e) => !seen.has(e.id))],
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        });
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        this.error = error;
      });
      console.error("[sla] failed to load more SLA activity", { slug, slaId, error });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }
}
